import { useMemo, useState } from 'react';
import { useTemplate } from '../../../../../features/template/TemplateProvider';
import { ButtonCancel, ButtonPrimary } from '../../../../../utils/Button';
import FocusControl from '../../../../../utils/FocusControl';
import InputField, { SelectOption, addOption } from '../../../../../utils/InputField';
import { TextCapitalize } from '../../../../../utils/TextFormatter';
import cn from '../../../../../utils/cn';
import { ApiCreateServicePackagePricing } from '../../ServiceOperation';

// const durationOptions = [
//     { key: 1, value: '1 Month' },
//     { key: 3, value: '3 Months' },
//     { key: 6, value: '6 Months' },
//     { key: 12, value: '1 Year' },
//     { key: 24, value: '2 Years' },
//     { key: 36, value: '3 Years' },
//     { key: 48, value: '4 Years' },
//     { key: 60, value: '5 Years' }
// ];

const billingCycle = [
    { key: 1, value: 'monthly' },
    { key: 3, value: 'quarterly' },
    { key: 6, value: 'half-yearly' },
    { key: 12, value: 'yearly' }
];

const initialData = {
    Currency: '',
    LimitByValue: 0,
    Amount: 0,
    VatPercent: 0,
    Status: 'active'
};

function AddPackagePriceModal({ closeModal, handleReload, packageInfo, serviceInfo }) {
    const [formData, setFormData] = useState({
        ...initialData,
        AppServiceId: serviceInfo?.id,
        PackageId: packageInfo.id,
        LimitByValue: 12
    });
    const [loading, setLoading] = useState(false);
    const [isError, setIsError] = useState('');
    const { setMessage } = useTemplate();
    const [isFlexible, setIsFlexible] = useState(false);
    const [isFree, setIsFree] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name) {
            setFormData((prevData) => ({ ...prevData, [name]: value }));
        }
    };
    const handleFlexible = (e) => {
        if (e.target.checked) {
            setIsFlexible(true);
            setFormData((prevData) => ({ ...prevData, Amount: 'Flexible' }));
        } else {
            setIsFlexible(false);
            setFormData((prevData) => ({ ...prevData, Amount: 0 }));
        }
    };

    const handleFree = (e) => {
        if (e.target.checked) {
            setIsFree(true);
            setFormData((prevData) => ({ ...prevData, Amount: 'Free' }));
        } else {
            setIsFree(false);
            setFormData((prevData) => ({ ...prevData, Amount: 0 }));
        }
    };

    const payload = useMemo(() => {
        if (isFlexible) {
            return {
                ...formData,
                LimitByValue: Number(formData?.LimitByValue) || 0,
                VatPercent: parseFloat(formData?.VatPercent) || 0,
                Amount: -101
            };
        }
        if (isFree) {
            return {
                ...formData,
                LimitByValue: Number(formData?.LimitByValue) || 0,
                VatPercent: parseFloat(formData?.VatPercent) || 0,
                Amount: -102
            };
        }
        return {
            ...formData,
            Amount: parseFloat(formData?.Amount) || 0,
            LimitByValue: Number(formData?.LimitByValue) || 0,
            VatPercent: parseFloat(formData?.VatPercent) || 0
        };
    }, [formData, isFlexible]);

    const handleAdd = (e) => {
        setLoading(true);
        e.preventDefault();
        ApiCreateServicePackagePricing(serviceInfo?.id, packageInfo?.id, payload, setIsError)
            .then(({ title }) => {
                setLoading(false);
                setMessage({ type: 'success', text: title });
                closeModal();
                handleReload();
            })
            .catch((error) => {
                setLoading(false);
                setMessage({ type: 'error', text: error.toString(0) });
            });
    };

    FocusControl(isError, setIsError, formData);

    return (
        <form onSubmit={handleAdd} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4">
                <SelectOption
                    label="Currency"
                    name="Currency"
                    formData={formData}
                    onChange={handleChange}
                    options={['thb', 'usd', 'eur', 'bdt']?.map((item) =>
                        addOption(item, item?.toUpperCase(), item.toUpperCase())
                    )}
                    required
                />
                {serviceInfo?.isDuration && (
                    <SelectOption
                        label="Duration"
                        name="LimitByValue"
                        formData={formData}
                        onChange={handleChange}
                        options={billingCycle?.map(({ key, value }) =>
                            addOption(key, TextCapitalize(value), key)
                        )}
                        disabled
                    />
                )}

                <div className={cn('flex flex-col w-full custom-field relative')}>
                    <div className="flex justify-between items-center pl-1 mb-1 text-sm md:text-base capitalize w-full font-normal text-left">
                        <span>
                            Amount <span className="text-error"> *</span>
                        </span>
                        <span className="flex items-center gap-4">
                            <label
                                className={cn('flex justify-between items-center gap-2', {
                                    'cursor-not-allowed opacity-50': isFree
                                })}
                            >
                                <span>Flexible</span>
                                <input
                                    type="checkbox"
                                    name="isFlexible"
                                    onChange={handleFlexible}
                                    className="mr-1"
                                    disabled={isFree}
                                    checked={isFlexible}
                                />
                            </label>
                            <label
                                className={cn('flex justify-between items-center gap-2', {
                                    'cursor-not-allowed opacity-50': isFlexible
                                })}
                            >
                                <span>Free</span>
                                <input
                                    type="checkbox"
                                    name="isFree"
                                    onChange={handleFree}
                                    className={cn('mr-1')}
                                    disabled={isFlexible}
                                    checked={isFree}
                                />
                            </label>
                        </span>
                    </div>
                    <input
                        type="text"
                        name="Amount"
                        value={formData.Amount}
                        onChange={handleChange}
                        placeholder="Amount"
                        readOnly={isFlexible || isFree}
                        autoComplete="off"
                        className={cn(
                            `flex-shrink w-full h-8.5 md:h-10 bg-transparent rounded-lg block outline-none border border-primary-200 px-2 text-primary text-xs md:text-sm leading-6 font-normal read-only:border-primary-100 read-only:bg-primary-50/50 read-only:text-primary-700 read-only:cursor-default`
                        )}
                    />
                    <ul className="text-sm text-error opacity-80 pl-4 mt-2">
                        <li className="list-disc">
                            If you select flexible, the amount will automatically be private and
                            will not be visible to the customer.
                        </li>
                        <li className="list-disc">
                            If you select free, the amount will be 0 and the service will be free of
                            charge.
                        </li>
                    </ul>
                </div>
                <InputField
                    label="VAT %"
                    name="VatPercent"
                    placeholder="Type vat percent..."
                    formData={formData}
                    onChange={handleChange}
                    optional
                />
            </div>

            <div className="flex gap-x-2 justify-end">
                <ButtonCancel title="Cancel" onClick={closeModal} />
                <ButtonPrimary title="Add" btnType="submit" loading={loading} />
            </div>
        </form>
    );
}

export default AddPackagePriceModal;
